import { Controller } from "@hotwired/stimulus"

// Stimulus controller for interval recording during ABA therapy sessions
// Provides a timer with visual/audio signals for tracking behavior occurrences
export default class extends Controller {
  static targets = [
    "currentInterval",
    "timeRemaining",
    "progressBar",
    "yesButton",
    "noButton",
    "startButton",
    "stopButton",
    "intervalsList",
    "statistics",
    "dataField"
  ]

  static values = {
    duration: Number,          // Interval duration in seconds
    type: String,              // Recording type: partial_interval, whole_interval, momentary_time_sampling
    lessonSkillId: Number,     // ID of the lesson skill
    autoAdvance: Boolean       // Auto advance to next interval
  }

  connect() {
    this.currentIntervalNumber = 0
    this.timeLeft = 0
    this.intervalTimer = null
    this.isRunning = false
    this.intervals = []

    // Load existing data from hidden field if present
    this.loadFromHiddenField()

    // Sound for interval change (optional)
    this.beepSound = new Audio('data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2/LDciUFLIHO8tiJNwgZaLvt559NEAxQp+PwtmMcBjiR1/LMeSwFJHfH8N2QQAoUXrTp66hVFApGn+DyvmwhBSp90O/aizsKG2W86+qnUhQOSpzj8MBtH3==')
  }

  disconnect() {
    this.stopRecording()
  }

  // Start interval recording session
  startRecording() {
    if (this.isRunning) return

    this.isRunning = true
    this.currentIntervalNumber = 1
    this.timeLeft = this.durationValue

    this.startButtonTarget.disabled = true
    this.stopButtonTarget.disabled = false
    this.yesButtonTarget.disabled = false
    this.noButtonTarget.disabled = false

    this.updateDisplay()
    this.startInterval()
    this.flashVisualSignal('start')
  }

  // Stop interval recording session
  stopRecording() {
    if (!this.isRunning) return

    this.isRunning = false
    clearInterval(this.intervalTimer)

    this.startButtonTarget.disabled = false
    this.stopButtonTarget.disabled = true
    this.yesButtonTarget.disabled = true
    this.noButtonTarget.disabled = true

    this.saveSession()
  }

  // Start countdown timer for current interval
  startInterval() {
    this.intervalTimer = setInterval(() => {
      this.timeLeft--
      this.updateDisplay()

      if (this.timeLeft <= 0) {
        this.onIntervalComplete()
      }
    }, 1000)
  }

  // Handle interval completion
  onIntervalComplete() {
    clearInterval(this.intervalTimer)

    // Visual and audio signal
    this.flashVisualSignal('interval-end')
    this.playBeep()

    // Auto-advance to next interval if enabled
    if (this.autoAdvanceValue) {
      setTimeout(() => {
        this.advanceToNextInterval()
      }, 1000)
    }
  }

  // Advance to next interval
  advanceToNextInterval() {
    if (!this.isRunning) return

    this.currentIntervalNumber++
    this.timeLeft = this.durationValue
    this.updateDisplay()
    this.startInterval()
    this.flashVisualSignal('interval-start')
  }

  // Record behavior as present in current interval
  recordYes() {
    this.recordInterval(this.currentIntervalNumber, true)
    this.markButtonActive(this.yesButtonTarget)

    if (this.autoAdvanceValue) {
      setTimeout(() => this.advanceToNextInterval(), 500)
    }
  }

  // Record behavior as absent in current interval
  recordNo() {
    this.recordInterval(this.currentIntervalNumber, false)
    this.markButtonActive(this.noButtonTarget)

    if (this.autoAdvanceValue) {
      setTimeout(() => this.advanceToNextInterval(), 500)
    }
  }

  // Record interval data to server
  recordInterval(intervalNumber, behaviorOccurred) {
    const data = {
      interval_number: intervalNumber,
      behavior_occurred: behaviorOccurred
    }

    this.intervals.push({
      number: intervalNumber,
      behavior_occurred: behaviorOccurred,
      timestamp: new Date().toISOString()
    })

    this.updateStatistics()
    this.saveToHiddenField()

    // Send to server via AJAX only if lesson_skill exists
    if (this.lessonSkillIdValue && this.lessonSkillIdValue > 0) {
      fetch(`/teachers/lesson_skills/${this.lessonSkillIdValue}/record_interval`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
        },
        body: JSON.stringify(data)
      })
      .catch(error => console.error('Error recording interval:', error))
    }
  }

  // Update display elements
  updateDisplay() {
    // Update current interval number
    if (this.hasCurrentIntervalTarget) {
      this.currentIntervalTarget.textContent = this.currentIntervalNumber
    }

    // Update time remaining
    if (this.hasTimeRemainingTarget) {
      this.timeRemainingTarget.textContent = this.formatTime(this.timeLeft)
    }

    // Update progress bar
    if (this.hasProgressBarTarget) {
      const percentage = ((this.durationValue - this.timeLeft) / this.durationValue) * 100
      this.progressBarTarget.style.width = `${percentage}%`

      // Change color based on time remaining
      if (this.timeLeft <= 5) {
        this.progressBarTarget.classList.remove('bg-primary', 'bg-warning')
        this.progressBarTarget.classList.add('bg-danger')
      } else if (this.timeLeft <= 10) {
        this.progressBarTarget.classList.remove('bg-primary', 'bg-danger')
        this.progressBarTarget.classList.add('bg-warning')
      } else {
        this.progressBarTarget.classList.remove('bg-warning', 'bg-danger')
        this.progressBarTarget.classList.add('bg-primary')
      }
    }
  }

  // Update statistics display
  updateStatistics() {
    if (!this.hasStatisticsTarget) return

    const totalIntervals = this.intervals.length
    const intervalsWithBehavior = this.intervals.filter(i => i.behavior_occurred).length
    const percentage = totalIntervals > 0 ? (intervalsWithBehavior / totalIntervals * 100).toFixed(1) : 0

    this.statisticsTarget.innerHTML = `
      <div class="grid grid-cols-3 gap-4 text-center">
        <div>
          <div class="text-2xl font-bold">${totalIntervals}</div>
          <div class="text-xs" style="color:var(--color-gray-400)">Total Intervals</div>
        </div>
        <div>
          <div class="text-2xl font-bold">${intervalsWithBehavior}</div>
          <div class="text-xs" style="color:var(--color-gray-400)">With Behavior</div>
        </div>
        <div>
          <div class="text-2xl font-bold" style="color:var(--color-primary)">${percentage}%</div>
          <div class="text-xs" style="color:var(--color-gray-400)">Percentage</div>
        </div>
      </div>
    `
  }

  // Flash visual signal
  flashVisualSignal(type) {
    const body = document.body
    let colorClass = ''

    switch(type) {
      case 'start':
        colorClass = 'bg-success'
        break
      case 'interval-end':
        colorClass = 'bg-warning'
        break
      case 'interval-start':
        colorClass = 'bg-info'
        break
    }

    // Flash background color
    body.classList.add(colorClass, 'bg-opacity-25')
    setTimeout(() => {
      body.classList.remove(colorClass, 'bg-opacity-25')
    }, 300)

    // Flash again for emphasis
    setTimeout(() => {
      body.classList.add(colorClass, 'bg-opacity-25')
      setTimeout(() => {
        body.classList.remove(colorClass, 'bg-opacity-25')
      }, 300)
    }, 400)
  }

  // Play beep sound
  playBeep() {
    if (this.beepSound) {
      this.beepSound.play().catch(e => console.log('Audio play failed:', e))
    }
  }

  // Mark button as active
  markButtonActive(button) {
    // Remove active from all buttons
    this.yesButtonTarget.classList.remove('active')
    this.noButtonTarget.classList.remove('active')

    // Add active to clicked button
    button.classList.add('active')

    // Remove after animation
    setTimeout(() => {
      button.classList.remove('active')
    }, 1000)
  }

  // Format seconds as MM:SS
  formatTime(seconds) {
    const mins = Math.floor(seconds / 60)
    const secs = seconds % 60
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
  }

  // Save session data to server
  saveSession() {
    this.saveToHiddenField()

    if (this.lessonSkillIdValue && this.lessonSkillIdValue > 0) {
      fetch(`/teachers/lesson_skills/${this.lessonSkillIdValue}/complete_interval_recording`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
        }
      })
      .catch(error => console.error('Error saving session:', error))
    }
  }

  // Save interval data to hidden field for form submission
  saveToHiddenField() {
    if (!this.hasDataFieldTarget) return

    const totalIntervals = this.intervals.length
    const intervalsWithBehavior = this.intervals.filter(i => i.behavior_occurred).length
    const percentage = totalIntervals > 0 ? (intervalsWithBehavior / totalIntervals * 100).toFixed(1) : 0

    const data = {
      intervals: this.intervals,
      total_intervals: totalIntervals,
      intervals_with_behavior: intervalsWithBehavior,
      percentage: parseFloat(percentage),
      duration: this.durationValue,
      type: this.typeValue
    }

    this.dataFieldTarget.value = JSON.stringify(data)
  }

  // Load interval data from hidden field on page load
  loadFromHiddenField() {
    if (!this.hasDataFieldTarget) return
    if (!this.dataFieldTarget.value) return

    try {
      const data = JSON.parse(this.dataFieldTarget.value)
      this.intervals = data.intervals || []
      if (this.intervals.length > 0) {
        this.updateStatistics()
      }
    } catch (error) {
      console.error('Error loading interval data:', error)
    }
  }
}
